import React, { useState, useEffect, useContext } from "react";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Notification from "../../components/UI/Notification";
import DeleteButton from "../../components/UI/AlertDialog";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaShieldAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

const ManageRoles = () => {
  const { roles, loadRoles, deleteARole, loading } = useContext(RoleContext);
  const { roles: userRoles } = useContext(AuthContext);
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate(); // For navigation to the edit role page
  const [notification, setNotification] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Helper function to generate role-based routes
  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, userRoles);
  };

  useEffect(() => {
    if (token) {
      loadRoles(token);
    }
  }, [token, loadRoles]);

  // Log roles whenever they change
  useEffect(() => {}, [roles]);

  const handleEditRole = (roleId) => {
    navigate(getRoleBasedRouteLocal(`/admin/edit-role/${roleId}`)); // Navigate to the edit role page with the role ID
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await deleteARole(token, roleId);
      // setNotification("Role deleted");
      setNotification({
        message: "Role deleted",
        type: "success",
      });
    } catch {
      // Errors are handled via the `error` state in the context
      setNotification({
        message: "Failed to delete Role",
        type: "error",
      });
    }
  };

  const filteredRoles = roles.filter((role) => {
    const roleName = (role.name || String(role)).toLowerCase();
    const matchesSearch = roleName.includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (role.status || "Active").toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="w-full">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="w-full dark:bg-gray-900 rounded p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h2 className="md:text-2xl text-xl font-semibold text-gray-800 dark:text-white shrink-0">
            Manage Roles
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4 w-full">
          {/* Search Bar - Left aligned and wider */}
          <div className="relative w-full sm:w-1/2 lg:w-[500px]">
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <FaTimes />
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <button
              onClick={() =>
                navigate(getRoleBasedRouteLocal("/admin/add-role"))
              }
              className="w-full sm:w-auto px-6 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md transition duration-200 flex items-center justify-center gap-2 shadow-sm shrink-0 whitespace-nowrap"
            >
              <FaPlus /> Create New Role
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3 mb-6">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
            {statusFilter === "all"
              ? "All Active Roles"
              : `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Roles`}
          </h3>
          <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold px-2.5 py-1 rounded-full">
            {filteredRoles.length}
          </span>
        </div>

        {/* <hr className="mb-6 opacity-50"></hr> */}

        {loading ? (
          <p className="text-gray-500 text-center dark:text-white">
            Loading roles...
          </p>
        ) : filteredRoles.length === 0 ? (
          <p className="text-gray-500 dark:text-white text-center py-10">
            No roles found matching your filters.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredRoles.map((role) => (
              <div
                key={role.id}
                className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-900/50 hover:shadow-lg transition-all group relative overflow-hidden flex flex-col justify-between"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 dark:bg-blue-900/10 rounded-full -mr-12 -mt-12 transition-all group-hover:scale-110"></div>

                <div className="relative flex gap-x-5">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                    <FaShieldAlt size={20} />
                  </div>
                  <h4 className="text-lg font-bold text-gray-800 dark:text-white break-words">
                    {role.name || role}
                  </h4>
                </div>

                <div className="flex items-center gap-3 mt-3 pt-4 border-t border-gray-100 dark:border-gray-700 relative z-10">
                  <button
                    onClick={() => handleEditRole(role.id)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-600 dark:hover:text-white rounded-lg transition-all"
                  >
                    <FaEdit size={14} /> Edit
                  </button>
                  <div className="flex-1">
                    <DeleteButton
                      delete_icon={
                        <>
                          <FaTrash size={14} /> Delete
                        </>
                      }
                      style={`w-full flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-600 hover:text-white dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-600 dark:hover:text-white rounded-lg transition-all`}
                      onDelete={() => handleDeleteRole(role.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageRoles;
